import React from 'react'
import { Button, Col, Dropdown, Input, Menu, Row, Tooltip } from 'antd'
import styled from '@emotion/styled'
import isNumber from 'lodash/isNumber'
import isEmpty from 'lodash/isEmpty'
import {
  CloseCircleOutlined,
  DownOutlined,
  PoundOutlined
} from '@ant-design/icons'
import { DeliveryPricingBand, PricingBandFieldType } from './types'
import { ValidateDeliveryPricingBand } from './utils'
import { PercentageOutlined } from '@ant-design/icons'
import { onFocusScrollNumber } from 'components/Utils/price'

interface PricingBandRowProps {
  pricingBand: DeliveryPricingBand
  pricingBandIndex: number
  onChange: ({
    fieldName,
    pricingBandIndex,
    value
  }: {
    fieldName: string
    pricingBandIndex: number
    value: number | null
  }) => void
  onRemove: () => void
  onlyRow: boolean
  isLastRow: boolean
  disabled?: boolean
}

interface ThresholdInputProps {
  value: number
  testId: string
  isDisabled: boolean
  fieldName: PricingBandFieldType
}

const PricingBandRow = ({
  pricingBand,
  disabled = false,
  onChange,
  onRemove,
  pricingBandIndex,
  onlyRow,
  isLastRow
}: PricingBandRowProps) => {
  const { lowerThreshold, upperThreshold, percentageDiscount } = pricingBand
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, validationFailMessages] = ValidateDeliveryPricingBand({
    pricingBand
  })

  const ThresholdInput = ({
    value,
    testId,
    isDisabled,
    fieldName
  }: ThresholdInputProps) => {
    const withInvalidUpperThresholdRange =
      validationFailMessages['withInvalidUpperThresholdRange']
    const withInvalidLowerThresholdRange =
      validationFailMessages['withInvalidLowerThresholdRange']
    const upperThresholdFail =
      fieldName === 'upperThreshold' && withInvalidUpperThresholdRange
    const lowerThresholdFail =
      fieldName === 'lowerThreshold' && withInvalidLowerThresholdRange
    let message = ''

    switch (fieldName) {
      case 'upperThreshold':
        message = withInvalidUpperThresholdRange
          ? `Your upper limit for this delivery pricing band cannot be lower than £${lowerThreshold}`
          : ''
        break
      case 'lowerThreshold':
        message = withInvalidLowerThresholdRange
          ? `Your lower limit for this delivery pricing band cannot be higher than £${upperThreshold}`
          : ''
        break
      default:
        message = ''
        break
    }

    const className =
      upperThresholdFail || (!isDisabled && lowerThresholdFail)
        ? `${fieldName} -error`
        : `${fieldName}`

    return (
      <Tooltip title={message}>
        <FullWidthInput
          className={className}
          disabled={isDisabled}
          type='number'
          addonAfter={<PoundOutlined />}
          data-testid={testId}
          min={0}
          defaultValue={value}
          onFocus={onFocusScrollNumber}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            event.target.removeEventListener('wheel', (e: Event) =>
              e.preventDefault()
            )
            const val = event.target.value
            const normalizedValue = isNumber(val) ? val : parseInt(val)
            onChange({
              fieldName,
              pricingBandIndex,
              value: normalizedValue
            })
          }}
        />
      </Tooltip>
    )
  }

  const PercentageInput = ({
    value,
    testId,
    isDisabled,
    fieldName
  }: ThresholdInputProps) => {
    const invalidPercentageDiscount =
      validationFailMessages['withInvalidPercentageDiscount']
    const invalidPercentageDiscountRange =
      validationFailMessages['withInvalidPercentageDiscountRange']
    const percentageFail =
      invalidPercentageDiscount || invalidPercentageDiscountRange
    const className = percentageFail ? '-error' : undefined
    const message = percentageFail && 'The discount % should be between 1-100%'

    return (
      <Tooltip title={message}>
        <FullWidthInput
          disabled={isDisabled}
          className={className}
          type='number'
          addonAfter={<PercentageOutlined />}
          data-testid={testId}
          defaultValue={value}
          onFocus={onFocusScrollNumber}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            event.target.removeEventListener('wheel', (e: Event) =>
              e.preventDefault()
            )
            const normalizedValue = isEmpty(event.target.value)
              ? null
              : parseInt(event.target.value)
            onChange({
              fieldName,
              pricingBandIndex,
              value: normalizedValue
            })
          }}
        />
      </Tooltip>
    )
  }

  const isLastRowAndUpwards =
    isLastRow &&
    (upperThreshold == null || upperThreshold.toString().length === 0
      ? true
      : false)

  const isLastRowWithValue =
    isLastRow &&
    (upperThreshold == null || upperThreshold.toString().length === 0
      ? false
      : true)

  const UpperThresholdMenu = (
    <Menu
      className='-plain'
      onClick={({ key }: { key: string }) => {
        onChange({
          fieldName: 'upperThreshold',
          pricingBandIndex,
          value: null
        })
      }}
    >
      <Menu.Item key='and-upwards'>and upwards</Menu.Item>
    </Menu>
  )

  const UpperThresholdInput = () => {
    const withInvalidUpperThresholdRange =
      !!validationFailMessages['withInvalidUpperThresholdRange']
    const dropdownClassname = withInvalidUpperThresholdRange
      ? 'upperThreshold error'
      : 'upperThreshold'

    return isLastRowWithValue ? (
      <Row align='middle'>
        <Col span={20} className='upper-threshold-input-col'>
          <ThresholdInput
            testId='pricing-band-row-to-field'
            value={upperThreshold}
            isDisabled={disabled || !isLastRow}
            fieldName='upperThreshold'
          />
        </Col>
        <Col span={4} className='upper-threshold-dropdown-col'>
          <Dropdown
            overlay={UpperThresholdMenu}
            trigger={['hover']}
            className={dropdownClassname}
          >
            <div>
              <DownOutlined style={{ fontSize: '12px', cursor: 'pointer' }} />
            </div>
          </Dropdown>
        </Col>
      </Row>
    ) : (
      <ThresholdInput
        testId='pricing-band-row-to-field'
        value={upperThreshold}
        isDisabled={disabled || !isLastRow}
        fieldName='upperThreshold'
      />
    )
  }

  return (
    <>
      <DeliveryPricingBandRow
        gutter={[4, 0]}
        data-testid='delivery-pricing-bands-row'
      >
        <Col span={5}>
          <ThresholdInput
            testId='pricing-band-row-from-field'
            value={lowerThreshold}
            isDisabled={disabled || !onlyRow}
            fieldName='lowerThreshold'
          />
        </Col>
        <Col span={5}>
          {isLastRowAndUpwards ? (
            <Row
              gutter={[4, 0]}
              className='_mb-0 _ht-36 upwards-upper-threshold'
              align='middle'
              justify='center'
              onClick={() => {
                onChange({
                  fieldName: 'upperThreshold',
                  pricingBandIndex,
                  value: 0
                })
              }}
            >
              <Col>and upwards</Col>
              <Col>
                <DownOutlined />
              </Col>
            </Row>
          ) : (
            <UpperThresholdInput />
          )}
        </Col>
        <Col span={5}>
          <PercentageInput
            value={percentageDiscount}
            testId='pricing-band-row-discount-field'
            isDisabled={disabled}
            fieldName='percentageDiscount'
          />
        </Col>
        <Col span={1}>
          {isLastRow && (
            <Button
              icon={<CloseCircleOutlined />}
              disabled={disabled}
              type='link'
              data-testid='pricing-band-row-remove-btn'
              className='-primary -plain'
              onClick={onRemove}
            />
          )}
        </Col>
      </DeliveryPricingBandRow>
    </>
  )
}

const FullWidthInput = styled(Input)`
  width: 100%;
`

const DeliveryPricingBandRow = styled(Row)`
  .ant-input-number.-error {
    border-color: #ff4d4f;
  }

  .ant-input-group-wrapper.-error {
    input[data-testid='pricing-band-row-discount-field'],
    input[data-testid='pricing-band-row-from-field'],
    input[data-testid='pricing-band-row-to-field'] {
      border-color: #ff4d4f;
    }

    .ant-input-affix-wrapper {
      border-color: #ff4d4f;
    }
  }

  .ant-input-group-wrapper.upperThreshold {
    .ant-input[type='number']:not(:disabled) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right: none;
    }
  }

  .ant-dropdown-trigger.upperThreshold {
    padding: 7px 4px;
    border: 1px solid #dbd9df;
    border-radius: 4px;

    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: none;

    &.error {
      border-color: #ff4d4f;
    }
  }

  .upwards-upper-threshold {
    cursor: pointer;
  }
`

export default PricingBandRow
